<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { isOnline } from '@/services/healthCheck'
import SafeJsonPretty from './SafeJsonPretty.vue'

const authStore = useAuthStore()

const logs = ref([])
const branchUsers = ref([])
const loading = ref(false)
const fetchError = ref('')

const filters = reactive({
  from: '',
  to: '',
  userId: '',
})

function close() {
  authStore.toggleErrorLogViewer()
}

function getHeaders() {
  const token = authStore.getActiveToken()
  const branchId = localStorage.getItem('branchId')
  return {
    Authorization: `Bearer ${token}`,
    'store-id': branchId,
  }
}

async function fetchBranchUsers() {
  if (!isOnline.value) { branchUsers.value = []; return }
  try {
    const response = await axios.get('/api/Users/BranchUsers', {
      headers: getHeaders(),
    })
    branchUsers.value = response.data ?? []
  } catch {
    // Non-critical — filter dropdown stays empty
    branchUsers.value = []
  }
}

async function fetchLogs() {
  if (!isOnline.value) {
    fetchError.value = 'Error logs require an internet connection.'
    logs.value = []
    return
  }
  loading.value = true
  fetchError.value = ''
  try {
    const params = {}
    if (filters.from) params.from = new Date(filters.from).toISOString()
    if (filters.to) params.to = new Date(filters.to).toISOString()
    if (filters.userId) params.userId = filters.userId

    const response = await axios.get('/api/ErrorLogger', {
      headers: getHeaders(),
      params,
    })
    // Attach reactive _showStorage toggle per row
    logs.value = (response.data ?? []).map(l => ({ ...l, _showStorage: false }))
  } catch {
    fetchError.value = 'Failed to load error logs. Please try again.'
    logs.value = []
  } finally {
    loading.value = false
  }
}

function clearFilters() {
  filters.from = ''
  filters.to = ''
  filters.userId = ''
  fetchLogs()
}

function formatDate(dateStr) {
  if (!dateStr) return '—'
  try {
    return new Date(dateStr).toLocaleString()
  } catch {
    return String(dateStr)
  }
}

function prettyStorage(raw) {
  if (!raw) return '—'
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

onMounted(() => {
  fetchBranchUsers()
  fetchLogs()
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div
        v-show="authStore.isErrorLogViewerVisible"
        class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center items-start px-8 z-[100]"
        @click.self="close"
      >
        <Transition name="modal-inner">
          <div
            v-if="authStore.isErrorLogViewerVisible"
            class="fixed top-16 z-[101] w-full max-w-5xl"
          >
            <div class="bg-white rounded-2xl shadow-lg p-6 w-full max-h-[85vh] overflow-y-auto">

              <!-- Header -->
              <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-3">
                  <h2 class="font-semibold text-[24px]">Error Log Viewer</h2>
                  <span class="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-md">
                    Last 30 days
                  </span>
                </div>
                <button type="button" @click="close" class="text-gray-500 hover:text-gray-700">
                  <i class="pi pi-times text-xl"></i>
                </button>
              </div>

              <!-- Filters -->
              <div class="flex gap-4 mb-6 flex-wrap items-end">
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-700">From</label>
                  <input
                    type="datetime-local"
                    v-model="filters.from"
                    class="border rounded px-2 py-1 text-sm"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-700">To</label>
                  <input
                    type="datetime-local"
                    v-model="filters.to"
                    class="border rounded px-2 py-1 text-sm"
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label class="text-sm font-medium text-gray-700">User</label>
                  <select v-model="filters.userId" class="border rounded px-2 py-1 text-sm min-w-[140px]">
                    <option value="">All users</option>
                    <option v-for="u in branchUsers" :key="u.id" :value="u.id">
                      {{ u.fullName }}
                    </option>
                  </select>
                </div>
                <button
                  @click="fetchLogs"
                  class="bg-blue-600 text-white px-4 py-1.5 rounded text-sm hover:bg-blue-700"
                >
                  Apply
                </button>
                <button
                  @click="clearFilters"
                  class="text-gray-500 text-sm hover:text-gray-700 underline"
                >
                  Clear
                </button>
              </div>

              <!-- Error -->
              <div v-if="fetchError" class="text-red-500 text-sm mb-4">
                {{ fetchError }}
              </div>

              <!-- Loading -->
              <div v-if="loading" class="text-gray-400 text-sm text-center py-8">
                Loading...
              </div>

              <!-- Empty -->
              <div
                v-else-if="!fetchError && logs.length === 0"
                class="text-gray-400 text-sm text-center py-8"
              >
                No error logs found for the selected filters.
              </div>

              <!-- Log entries -->
              <div v-else class="flex flex-col gap-4">
                <div
                  v-for="log in logs"
                  :key="log.id"
                  class="border border-gray-200 rounded-lg p-4 flex flex-col gap-3"
                >
                  <!-- Row header -->
                  <div class="flex justify-between items-center text-sm">
                    <span class="font-medium text-gray-700">
                      {{ log.userFullName || '—' }}
                    </span>
                    <span class="text-gray-400 text-xs">
                      {{ formatDate(log.loggedAt) }}
                    </span>
                  </div>

                  <!-- Call Stack -->
                  <div>
                    <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Call Stack
                    </div>
                    <pre
                      v-if="log.callStack"
                      class="text-xs bg-gray-50 rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-32 overflow-y-auto"
                    >{{ log.callStack }}</pre>
                    <span v-else class="text-gray-400 text-xs">—</span>
                  </div>

                  <!-- Error Details (vue-json-pretty via SafeJsonPretty) -->
                  <div>
                    <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      Error Details
                    </div>
                    <SafeJsonPretty :data="log.errorDetails" />
                  </div>

                  <!-- LocalStorage (collapsible pre block) -->
                  <div>
                    <button
                      @click="log._showStorage = !log._showStorage"
                      class="text-xs text-blue-600 hover:underline"
                    >
                      {{ log._showStorage ? 'Hide' : 'Show' }} LocalStorage
                    </button>
                    <pre
                      v-if="log._showStorage"
                      class="text-xs bg-gray-50 rounded p-2 mt-1 overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto"
                    >{{ prettyStorage(log.localStorage) }}</pre>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
